import { useState } from 'react';
import { useLang } from '../../../i18n/LanguageContext';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const consultationTypes = [
  { en: 'Tarot Reading', zh: '塔罗牌解读' },
  { en: 'Astrology & Birth Chart', zh: '占星与星盘解读' },
  { en: 'Crystal Healing', zh: '水晶疗愈' },
  { en: 'Chakra Energy Clearing', zh: '脉轮能量净化' },
  { en: 'Guided Meditation', zh: '引导冥想' },
  { en: 'General Consultation', zh: '综合灵性咨询' },
];

type FormData = {
  name: string;
  phone: string;
  email: string;
  consultationType: string;
  date: string;
  time: string;
  message: string;
};

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const { t } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    consultationType: '',
    date: '',
    time: '',
    message: '',
  });

  if (!isOpen) return null;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', phone: '', email: '', consultationType: '', date: '', time: '', message: '' });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const fd = new URLSearchParams();
    fd.append('name', formData.name);
    fd.append('phone', formData.phone);
    fd.append('email', formData.email);
    fd.append('consultationType', formData.consultationType);
    fd.append('date', formData.date);
    fd.append('time', formData.time);
    fd.append('message', formData.message);
    try {
      const res = await fetch('https://readdy.ai/api/form/d70l17mth28qd804ct10', {
        method: 'POST',
        body: fd,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch { /* silent */ }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div
        className="bg-[#1e0f35] rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto border border-[#d4af37]/40 relative"
        style={{ boxShadow: '0 0 60px rgba(212,175,55,0.2)' }}
      >
        {/* 顶部装饰光晕 */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent rounded-full"></div>

        {/* Header */}
        <div className="sticky top-0 bg-[#1e0f35]/95 backdrop-blur-sm px-6 py-5 flex items-center justify-between border-b border-[#d4af37]/20 z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#d4af37]/15">
              <i className="ri-calendar-check-line text-[#d4af37] text-lg"></i>
            </div>
            <div>
              <h3 className="text-lg font-serif text-white leading-tight">{t('Book a Consultation', '预约灵性咨询')}</h3>
              <p className="text-xs text-[#c8b6ff] mt-0.5">{t('Our advisor will contact you within 24 hours', '顾问将在24小时内与您联系')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-all duration-200 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-xl text-white/70"></i>
          </button>
        </div>

        <div className="px-6 py-6">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-4xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-serif text-white mb-2">{t('Booking Submitted!', '预约提交成功！')}</h3>
              <p className="text-[#c8b6ff] text-sm leading-relaxed mb-8">
                {t(
                  'Thank you! Our spiritual advisor will contact you within 24 hours to confirm your appointment.',
                  '感谢您的预约！我们的灵性顾问将在24小时内联系您确认预约时间。'
                )}
              </p>
              <div className="flex items-center justify-center space-x-3 mb-8 text-sm text-[#d4af37]">
                <i className="ri-sparkling-line"></i>
                <span>{t('May your journey be filled with light', '愿您的旅程充满光芒')}</span>
                <i className="ri-sparkling-line"></i>
              </div>
              <button
                onClick={handleClose}
                className="px-10 py-3 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] rounded-full cursor-pointer whitespace-nowrap font-bold text-sm hover:opacity-90 transition-all duration-300"
              >
                {t('Close', '关闭')}
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="consultation-booking-form"
              className="space-y-4"
            >
              {/* 姓名 */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {t('Your Name', '您的姓名')}
                  <span className="text-[#d4af37] ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm"
                  placeholder={t('Enter your full name', '请输入您的姓名')}
                />
              </div>

              {/* 联系方式两列 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1.5">
                    {t('Phone', '联系电话')}
                    <span className="text-[#d4af37] ml-1">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm"
                    placeholder={t('+44 xxxx xxx xxx', '请输入电话号码')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1.5">
                    {t('Email', '电子邮箱')}
                    <span className="text-[#d4af37] ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* 咨询类型 */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {t('Consultation Type', '咨询类型')}
                  <span className="text-[#d4af37] ml-1">*</span>
                </label>
                <select
                  name="consultationType"
                  value={formData.consultationType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm cursor-pointer appearance-none"
                  style={{ backgroundImage: 'none' }}
                >
                  <option value="" disabled className="bg-[#2d1b4e] text-white/50">
                    {t('Select consultation type', '请选择咨询类型')}
                  </option>
                  {consultationTypes.map((type) => (
                    <option key={type.en} value={t(type.en, type.zh)} className="bg-[#2d1b4e] text-white">
                      {t(type.en, type.zh)}
                    </option>
                  ))}
                </select>
              </div>

              {/* 日期时间 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1.5">
                    {t('Preferred Date', '期望日期')}
                    <span className="text-[#d4af37] ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1.5">
                    {t('Preferred Time', '期望时间')}
                    <span className="text-[#d4af37] ml-1">*</span>
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm cursor-pointer"
                  />
                </div>
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {t('Additional Notes', '补充说明')}
                  <span className="text-white/40 text-xs ml-2">{t('(Optional)', '（选填）')}</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 bg-[#2d1b4e]/60 border border-[#d4af37]/25 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/60 transition-all duration-200 text-sm resize-none"
                  placeholder={t('Any specific questions or topics you\'d like to explore... (max 500 characters)', '有什么特别想探讨的问题或主题请告诉我们...（最多500字）')}
                />
                <div className="text-right text-xs text-white/30 mt-1">{formData.message.length}/500</div>
              </div>

              {/* 提交按钮 */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:opacity-90 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    <span>{t('Submitting...', '提交中...')}</span>
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line text-base"></i>
                    <span>{t('Submit Booking Request', '提交预约申请')}</span>
                  </>
                )}
              </button>

              <p className="text-center text-xs text-white/30">
                {t('By submitting, you agree to our privacy policy', '提交即表示您同意我们的隐私政策')}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
